// src/components/sections/DevelopmentUpdatesSection.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Edit2, ExternalLink, Calendar, CheckCircle, Clock, Zap, GitBranch, Flag, MessageCircle
} from 'lucide-react';
import clsx from 'clsx';  

import {
  AgentData_V10,
  AgentHubPageViewMode,
  DevelopmentUpdatesSectionPublicV10,
  RoadmapItemPublicV10,
  NewsUpdateItemV10,
  TRoadmapItemStatusV10,
  TAgentDevelopmentStageV10,
} from '@dopomogai/types'; // All types can come from agent.ts
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { Badge } from '@dopomogai/ui';
import { INNER_CARD_ANIMATE_RESTING, INNER_CARD_HOVER, INNER_CARD_TRANSITION_HOVER } from '../../shared/animationConstants';

// --- Helper Functions (remain unchanged) ---
const getRoadmapStatusIcon = (status: TRoadmapItemStatusV10) => {
  const props = { size: 20, className: "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" };
  switch (status) {
    case 'Completed & Launched': return <CheckCircle {...props} />;
    case 'In Progress': return <Zap {...props} />;
    case 'Planned': return <Flag {...props} />;
    case 'Researching': return <GitBranch {...props} />;
    default: return <Clock {...props} />;
  }
};

const getRoadmapStatusColorClasses = (status: TRoadmapItemStatusV10): string => {
  switch (status) {
    case 'Completed & Launched': return 'bg-emerald-500 text-white';
    case 'In Progress': return 'bg-sky-500 text-white';
    case 'Planned': return 'bg-indigo-500 text-white';
    case 'Researching': return 'bg-amber-500 text-white';
    default: 'bg-slate-400 text-white';
  }
  return 'bg-slate-400 text-white'; // Added explicit return for all paths
};

// --- Sub-Components (remain unchanged) ---
const RoadmapTimeline: React.FC<{ items: RoadmapItemPublicV10[]; t: Function }> = ({ items, t }) => (
  // ... No changes needed here, its implementation is fine.
  <div className="relative pl-8">
    <div className="absolute left-0 top-0 bottom-0 w-1 bg-slate-200 rounded-full" aria-hidden="true" style={{ transform: 'translateX(-50%)' }}></div>
    <div className="space-y-12">
      {items.map((item, index) => (
        <motion.div key={item.id} className="relative group" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true, amount: 0.5 }}>
          <div className={clsx("absolute -left-[15.5px] top-1 w-8 h-8 rounded-full border-4 border-gray-50 flex items-center justify-center z-10", getRoadmapStatusColorClasses(item.status))}>
            {getRoadmapStatusIcon(item.status)}
          </div>
          <motion.div animate={INNER_CARD_ANIMATE_RESTING} whileHover={INNER_CARD_HOVER} transition={INNER_CARD_TRANSITION_HOVER} className="bg-white p-5 rounded-xl border-2 border-black relative z-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600 mb-1">{item.targetStage}</p>
            <h4 className="text-lg font-semibold text-gray-900 mb-1.5">{item.title}</h4>
            {item.description && <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>}
             {item.eta && (
                <p className="text-xs text-slate-500 font-medium mt-3 pt-3 border-t border-slate-100 flex items-center">
                    <Clock size={12} className="mr-1.5 opacity-70" /> ETA: {item.eta}
                </p>
              )}
          </motion.div>
        </motion.div>
      ))}
    </div>
  </div>
);

const NewsSnippets: React.FC<{ items: NewsUpdateItemV10[]; onAction: Function; }> = ({ items, onAction }) => (
  // ... No changes needed here, its implementation is fine.
  <div className="space-y-4">
    {items.map((item) => {
      const isClickable = !!item.fullArticleUrl;
      return (
        <motion.div key={item.id} animate={INNER_CARD_ANIMATE_RESTING} whileHover={isClickable ? INNER_CARD_HOVER : {}} transition={INNER_CARD_TRANSITION_HOVER} onClick={() => isClickable && onAction('VIEW_NEWS_ARTICLE', { url: item.fullArticleUrl })} className={clsx("bg-white p-5 rounded-xl border-2 border-black flex flex-col sm:flex-row gap-5 items-center", isClickable && 'cursor-pointer')}>
          {item.imageUrl && ( <img src={item.imageUrl} alt={item.title} className="w-full sm:w-40 h-40 sm:h-auto object-cover rounded-lg border-2 border-black flex-shrink-0" /> )}
          <div className="flex-grow">
            <p className="text-xs text-gray-500 mb-1 font-semibold">{new Date(item.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <h4 className="text-lg font-semibold text-gray-900 mb-1.5 leading-tight">{item.title}</h4>
            <p className="text-sm text-gray-600 leading-relaxed">{item.snippet}</p>
          </div>
        </motion.div>
      )
    })}
  </div>
);

const GetInvolvedCallout: React.FC<{ calloutData: { feedbackUrl: string, communityUrl: string }, stage: TAgentDevelopmentStageV10; t: Function; onAction: Function }> = ({ calloutData, stage, t, onAction }) => (
  // ... No changes needed here, its implementation is fine.
  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl border-2 border-black text-center mt-12">
    <MessageCircle size={32} className="text-indigo-500 mx-auto mb-3" />
    <h4 className="text-xl font-bold text-gray-800 mb-1">{t('developmentUpdatesSection.getInvolved.title')}</h4>
    <p className="text-gray-600 max-w-lg mx-auto mb-4">{t('developmentUpdatesSection.getInvolved.description', { stage })}</p>
    <div className="flex justify-center gap-3">
        <Button textKey="developmentUpdatesSection.getInvolved.contactButton" variant="secondary" onClick={() => onAction('OPEN_URL', { url: calloutData.feedbackUrl })}/>
        <Button textKey="developmentUpdatesSection.getInvolved.joinCommunityButton" variant="outline" onClick={() => onAction('OPEN_URL', { url: calloutData.communityUrl })}/>
    </div>
  </div>
);


// --- REFACTORED Main Component ---

// The props are now more flexible to handle data from either page type.
interface DevelopmentUpdatesSectionProps {
  /** The full agent data object (passed from AgentHubPage) */
  agentData?: AgentData_V10;
  /** A direct data slice for this section (passed from MultiAgentSolutionPage) */
  data?: DevelopmentUpdatesSectionPublicV10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

export const DevelopmentUpdatesSection = React.forwardRef<HTMLDivElement, DevelopmentUpdatesSectionProps>(
  ({ agentData, data, viewMode, onAction, headerBgColor, id }, ref) => {
    const { t } = useTranslation('agent-common');
    const isOwnerView = viewMode === 'owner_dashboard';

    // *** THE CORE OF THE REFACTOR ***
    // We determine the sectionData from either the direct `data` prop or the `agentData` prop.
    // This makes the component agnostic to where its data comes from.
    const sectionData = data || agentData?.developmentUpdatesSection;

    // The owner view logic now also uses the unified `sectionData`.
    if (isOwnerView) {
      // If there's no data even in owner view, show a configuration prompt.
      if (!sectionData) {
        return (
          <Card
            id={id} ref={ref}
            titleKey="developmentUpdatesSection.ownerTitle"
            icon={<Calendar size={22} className="text-cyan-600" />}
            headerBgColor={headerBgColor}
          >
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No development updates or roadmap configured for this entity.</p>
              <Button text="Configure Updates & Roadmap" icon={<Edit2 size={16}/>} onClick={() => onAction('MANAGE_DEVELOPMENT_UPDATES', {})} />
            </div>
          </Card>
        );
      }
      // If there is data, render the owner view with management buttons.
      // NOTE: This part is a placeholder. You would build out the full owner view UI here.
      // For now, it just shows a message but uses the correct `sectionData` for its URLs.
      return (
         <Card
          id={id} ref={ref}
          titleKey="developmentUpdatesSection.ownerTitle"
          icon={<Calendar size={22} className="text-cyan-600" />}
          headerBgColor={headerBgColor}
          actionButton={(
            <div className="flex gap-2">
              {sectionData.manageRoadmapUrl && (
                <Button text="Manage Roadmap" variant='outline' size='sm' icon={<Edit2 size={14} />} onClick={() => onAction('MANAGE_ROADMAP', { url: sectionData.manageRoadmapUrl })} />
              )}
              {sectionData.manageNewsUrl && (
                <Button text="Manage News" variant='outline' size='sm' icon={<Edit2 size={14} />} onClick={() => onAction('MANAGE_NEWS', { url: sectionData.manageNewsUrl })} />
              )}
            </div>
          )}
        >
          <p className="text-center text-gray-400 italic py-8">Owner dashboard view for this section. (Render full owner UI here)</p>
        </Card>
      );
    }
    
    // If we're in public view and there's no data, render nothing.
    if (!sectionData) {
      return null;
    }
  
    // --- PUBLIC VIEW ---
    // The rest of the component uses the unified `sectionData` variable and remains unchanged.
    const { currentDevelopmentStage, publicRoadmap, latestNewsPublic, getInvolvedCallout } = sectionData;
    const roadmapItems = publicRoadmap?.items?.slice(0, 3) || [];
    const newsItems = latestNewsPublic?.items?.filter(item => item.isPublished).slice(0, 2) || [];

    return (
      <Card
        id={id} ref={ref}
        titleKey="developmentUpdatesSection.defaultTitle"
        icon={<Calendar size={22} className="text-cyan-600" />}
        headerBgColor={headerBgColor}
        actionButton={
          <Badge 
            text={currentDevelopmentStage} 
            accentColor="green" 
            icon={<Zap size={14}/>} 
          />
        }
      >
        <div className="space-y-12">
          {roadmapItems.length > 0 && (
            <section aria-labelledby="roadmap-heading">
              <h3 id="roadmap-heading" className="text-2xl font-bold text-gray-800 mb-6 text-center">{t('developmentUpdatesSection.productRoadmapTitle')}</h3>
              <RoadmapTimeline items={roadmapItems} t={t} />
              {publicRoadmap?.fullRoadmapUrl && (
                <div className="mt-8 text-center">
                  <Button 
                    textKey="developmentUpdatesSection.viewFullRoadmapButton" 
                    variant="secondary" 
                    icon={<ExternalLink size={16} />} 
                    iconPosition="right" 
                    onClick={() => onAction('VIEW_FULL_ROADMAP_PUBLIC', { url: publicRoadmap.fullRoadmapUrl })}
                  />
                </div>
              )}
            </section>
          )}
          
          {newsItems.length > 0 && roadmapItems.length > 0 && <hr className="border-slate-200/80 my-2" />}

          {newsItems.length > 0 && (
             <section aria-labelledby="news-heading">
              <h3 id="news-heading" className="text-2xl font-bold text-gray-800 mb-6 text-center">{t('developmentUpdatesSection.latestUpdatesTitle')}</h3>
              <NewsSnippets items={newsItems} onAction={onAction} />
              {latestNewsPublic?.allNewsUrl && (
                <div className="mt-8 text-center">
                  <Button 
                    textKey="developmentUpdatesSection.viewAllNewsButton" 
                    variant="secondary" 
                    icon={<ExternalLink size={16} />} 
                    iconPosition="right" 
                    onClick={() => onAction('VIEW_ALL_NEWS_PUBLIC', { url: latestNewsPublic.allNewsUrl })}
                  />
                </div>
              )}
            </section>
          )}

          {getInvolvedCallout && (
            <GetInvolvedCallout 
              calloutData={getInvolvedCallout} 
              stage={currentDevelopmentStage} 
              t={t} 
              onAction={onAction} 
            />
          )}
        </div>
      </Card>
    );
  }
);

DevelopmentUpdatesSection.displayName = 'DevelopmentUpdatesSection';
